/**
 * Shared retry and exponential backoff utilities.
 *
 * Used across:
 * - TanStack Query for REST API retries
 * - SSE connection retry logic
 * - Future WebSocket/long-lived connection retries
 */

/**
 * Calculate exponential backoff delay with a maximum cap.
 *
 * Formula: baseDelay * 2^attempt, capped at maxDelay
 *
 * @param attempt - Zero-indexed attempt number (0 = first retry)
 * @param baseDelay - Initial delay in milliseconds (default: 1000ms)
 * @param maxDelay - Maximum delay cap in milliseconds (default: 30000ms)
 * @returns Delay in milliseconds
 *
 * @example
 * ```typescript
 * // For SSE connections (1s base):
 * getExponentialBackoff(0) // 1000ms
 * getExponentialBackoff(1) // 2000ms
 * getExponentialBackoff(2) // 4000ms
 * getExponentialBackoff(3) // 8000ms
 * getExponentialBackoff(4) // 16000ms
 * getExponentialBackoff(5) // 30000ms (capped)
 *
 * // For REST API retries (2s base):
 * getExponentialBackoff(0, 2000) // 2000ms
 * getExponentialBackoff(1, 2000) // 4000ms
 * getExponentialBackoff(2, 2000) // 8000ms
 * ```
 */
export function getExponentialBackoff(
  attempt: number,
  baseDelay: number = 1000,
  maxDelay: number = 30000,
): number {
  return Math.min(baseDelay * 2 ** attempt, maxDelay);
}

/**
 * Retry strategy configuration for different connection types.
 */
export const RETRY_STRATEGIES = {
  /**
   * REST API retries via TanStack Query.
   * Quick fail for user feedback.
   */
  REST_API: {
    maxRetries: 1,
    baseDelay: 2000,
    maxDelay: 30000,
  },

  /**
   * SSE long-lived connection retries.
   * Persistent retry for background connections.
   */
  SSE_CONNECTION: {
    maxRetries: 5,
    baseDelay: 1000,
    maxDelay: 30000,
    cleanupDelay: 500, // Time for backend to unregister connection
  },

  /**
   * Future: WebSocket connection retries.
   * Similar to SSE but may need different parameters.
   */
  WEBSOCKET: {
    maxRetries: 5,
    baseDelay: 1000,
    maxDelay: 30000,
    cleanupDelay: 500,
  },
} as const;

/**
 * Get total delay including cleanup time for connection retries.
 * Used for SSE/WebSocket where backend needs time to clean up old connections.
 *
 * @param attempt - Zero-indexed attempt number
 * @param strategy - Retry strategy configuration
 * @returns Total delay including backoff + cleanup time
 *
 * @example
 * ```typescript
 * const delay = getConnectionRetryDelay(1, RETRY_STRATEGIES.SSE_CONNECTION);
 * // Returns: 2500ms (2000ms backoff + 500ms cleanup)
 * ```
 */
export function getConnectionRetryDelay(
  attempt: number,
  strategy: typeof RETRY_STRATEGIES.SSE_CONNECTION | typeof RETRY_STRATEGIES.WEBSOCKET,
): number {
  const backoffDelay = getExponentialBackoff(attempt, strategy.baseDelay, strategy.maxDelay);
  return backoffDelay + strategy.cleanupDelay;
}
